/*
 * Stay or Go? — admin panel.
 *
 * Behavioral parity with artifacts/prototypes/vendor-exit-diagnostic-admin.html,
 * but instead of writing to localStorage, this writes to the production
 * draft/publish API:
 *   - GET /admin/configs/stay_or_go/draft  → load current draft
 *   - PUT /admin/configs/stay_or_go/draft  → save draft (debounced on edit)
 *   - POST /admin/configs/stay_or_go/publish → promote draft to published
 *   - GET /admin/configs/stay_or_go/history → version list
 *   - POST /admin/configs/stay_or_go/rollback/{version} → revert
 *
 * The draft is server-side now (multi-admin safe), so the auth gating that
 * was missing from the prototype lives in staticwebapp.config.json (Phase 12).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ApiError, api } from '@shared/api/client';
import {
  DEFAULT_CONFIG,
  type StayOrGoConfig,
  type Question,
  type SpendTier,
  type StatItem,
  type TeamSize,
} from '@tools/stay-or-go/calc';
import type { HistoryEntry } from '@shared/types/api';
import {
  AdminShell,
  AdminSidebar,
  AdminSection,
  AdminSaveBar,
  AdminModal,
  AdminPublishModal,
  AdminHistoryList,
  AdminField,
  AdminNumberField,
  mergeDefaults,
  type SaveStatus,
  type AdminSectionMeta,
} from '@admin/_shared';
import './StayOrGoAdmin.css';

const TOOL_ID = 'stay_or_go';
const DRAFT_DEBOUNCE_MS = 700;

const COLOR_VARS: Array<{ name: string; label: string; default: string }> = [
  { name: '--primary', label: 'Primary (navy)', default: '#1e3a5f' },
  { name: '--primaryLight', label: 'Primary light', default: '#2d4a6f' },
  { name: '--primaryDark', label: 'Primary dark', default: '#152a45' },
  { name: '--secondary', label: 'Secondary (teal accent)', default: '#0d9488' },
  { name: '--secondaryLight', label: 'Secondary light', default: '#f0fdfa' },
  { name: '--accent', label: 'Accent (electric blue)', default: '#305cde' },
  { name: '--success', label: 'Success', default: '#10b981' },
  { name: '--warning', label: 'Warning', default: '#f59e0b' },
  { name: '--error', label: 'Error', default: '#dc2626' },
];

/** Admin config carries the full StayOrGoConfig plus the optional brand vars. */
interface AdminConfig extends StayOrGoConfig {
  vars?: Record<string, string>;
}

const DEFAULT_ADMIN_CONFIG: AdminConfig = {
  ...DEFAULT_CONFIG,
  vars: COLOR_VARS.reduce<Record<string, string>>((acc, c) => {
    acc[c.name] = c.default;
    return acc;
  }, {}),
};

const SECTIONS: ReadonlyArray<AdminSectionMeta> = [
  { id: 'brand', label: 'Brand' },
  { id: 'welcome', label: 'Welcome & nav' },
  { id: 'stats', label: 'Stats bar' },
  { id: 'questions', label: 'Questions' },
  { id: 'verdicts', label: 'Verdicts & thresholds' },
  { id: 'calc', label: 'Cost calculation' },
  { id: 'tiers', label: 'Spend tiers' },
  { id: 'teams', label: 'Team sizes' },
  { id: 'data', label: 'Data & reset' },
];

export default function StayOrGoAdmin() {
  const [config, setConfig] = useState<AdminConfig | null>(null);
  const [basedOnVersion, setBasedOnVersion] = useState<number>(0);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ kind: 'idle' });
  const [showResetModal, setShowResetModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishNote, setPublishNote] = useState('');
  const [activeSection, setActiveSection] = useState<string>('brand');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial load: prefer the current draft; if no draft, prefer published; else defaults.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const draft = await api.getDraft<AdminConfig>(TOOL_ID);
        if (draft && alive) {
          setConfig(mergeDefaults(DEFAULT_ADMIN_CONFIG, draft.config));
          setBasedOnVersion(draft.version);
          await refreshHistory();
          return;
        }
        const { snapshot } = await api.getPublishedConfig<AdminConfig>(TOOL_ID);
        if (snapshot && alive) {
          setConfig(mergeDefaults(DEFAULT_ADMIN_CONFIG, snapshot.config));
          setBasedOnVersion(snapshot.version);
          await refreshHistory();
          return;
        }
        if (alive) {
          setConfig(JSON.parse(JSON.stringify(DEFAULT_ADMIN_CONFIG)));
          setBasedOnVersion(0);
        }
      } catch (err: unknown) {
        if (alive) {
          setLoadError(
            err instanceof Error ? err.message : 'Failed to load admin config.',
          );
          setConfig(JSON.parse(JSON.stringify(DEFAULT_ADMIN_CONFIG)));
        }
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      const res = await api.getHistory(TOOL_ID);
      setHistory(res.history);
    } catch {
      /* history is optional; non-fatal */
    }
  }, []);

  /** Save current config as a draft. Server returns the new draft version. */
  const saveDraftNow = useCallback(
    async (next: AdminConfig) => {
      setSaveStatus({ kind: 'saving' });
      try {
        const res = await api.saveDraft<AdminConfig>(TOOL_ID, {
          config: next,
          based_on_version: basedOnVersion,
        });
        setBasedOnVersion(res.version);
        setSaveStatus({ kind: 'saved', at: Date.now() });
      } catch (err: unknown) {
        const message =
          err instanceof ApiError
            ? err.envelope?.error?.message ?? err.message
            : err instanceof Error
            ? err.message
            : 'Save failed.';
        setSaveStatus({ kind: 'error', message });
      }
    },
    [basedOnVersion],
  );

  // Whenever config changes, mark dirty and schedule a debounced save.
  const scheduleSave = useCallback(
    (next: AdminConfig) => {
      setSaveStatus({ kind: 'dirty' });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void saveDraftNow(next);
      }, DRAFT_DEBOUNCE_MS);
    },
    [saveDraftNow],
  );

  const update = useCallback(
    (mutator: (draft: AdminConfig) => void) => {
      setConfig((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev)) as AdminConfig;
        mutator(next);
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const onPublish = useCallback(async () => {
    if (!config) return;
    // Flush any pending debounced save first.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    try {
      // Save the latest draft, then publish it.
      const draftRes = await api.saveDraft<AdminConfig>(TOOL_ID, {
        config,
        based_on_version: basedOnVersion,
      });
      const pubRes = await api.publish<AdminConfig>(TOOL_ID, {
        based_on_version: draftRes.version,
        publish_note: publishNote || undefined,
      });
      setBasedOnVersion(pubRes.version);
      setSaveStatus({ kind: 'saved', at: Date.now() });
      setShowPublishModal(false);
      setPublishNote('');
      await refreshHistory();
    } catch (err: unknown) {
      const message =
        err instanceof ApiError
          ? err.envelope?.error?.message ?? err.message
          : err instanceof Error
          ? err.message
          : 'Publish failed.';
      setSaveStatus({ kind: 'error', message });
    }
  }, [config, basedOnVersion, publishNote, refreshHistory]);

  const onRollback = useCallback(
    async (version: number) => {
      try {
        const res = await api.rollback<AdminConfig>(TOOL_ID, version);
        setConfig(mergeDefaults(DEFAULT_ADMIN_CONFIG, res.config));
        setBasedOnVersion(res.version);
        await refreshHistory();
      } catch (err: unknown) {
        const message =
          err instanceof ApiError
            ? err.envelope?.error?.message ?? err.message
            : err instanceof Error
            ? err.message
            : 'Rollback failed.';
        setSaveStatus({ kind: 'error', message });
      }
    },
    [refreshHistory],
  );

  const onResetToDefaults = useCallback(() => {
    const fresh = JSON.parse(JSON.stringify(DEFAULT_ADMIN_CONFIG)) as AdminConfig;
    setConfig(fresh);
    scheduleSave(fresh);
    setShowResetModal(false);
  }, [scheduleSave]);

  const onExport = useCallback(() => {
    if (!config) return;
    const blob = new Blob([JSON.stringify(config, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download =
      'motr-stay-or-go-config-' + new Date().toISOString().slice(0, 10) + '.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [config]);

  const onImport = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const incoming = JSON.parse(String(reader.result));
          const merged = mergeDefaults(DEFAULT_ADMIN_CONFIG, incoming);
          setConfig(merged);
          scheduleSave(merged);
        } catch (err: unknown) {
          alert('Import failed: ' + (err instanceof Error ? err.message : String(err)));
        }
      };
      reader.readAsText(file);
    },
    [scheduleSave],
  );

  if (loadError && !config) {
    return (
      <div className="adm-page">
        <div className="adm-content">
          <div className="adm-banner-error">{loadError}</div>
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="adm-page">
        <div className="adm-loading">Loading admin…</div>
      </div>
    );
  }

  const saveBar = (
    <AdminSaveBar
      status={saveStatus}
      canPublish={Boolean(config)}
      onPublishClick={() => setShowPublishModal(true)}
    />
  );

  return (
    <>
      <AdminShell
        subnavTag="Stay or Go? — Admin"
        publicHref="/stay-or-go"
        onExport={onExport}
        loadError={loadError}
        savebar={saveBar}
      >
        <AdminSidebar sections={SECTIONS} active={activeSection} onSelect={setActiveSection} />

        <main className="adm-content">
          <AdminSection
            id="brand"
            title="Brand"
            sub="Override the brand colors used across the public tool."
            onEnterView={setActiveSection}
          >
            {COLOR_VARS.map((c) => (
              <div className="adm-color-row" key={c.name}>
                <input
                  type="color"
                  className="adm-input adm-input--color"
                  id={`c-${c.name}`}
                  value={(config.vars && config.vars[c.name]) || c.default}
                  onChange={(e) =>
                    update((draft) => {
                      draft.vars = { ...(draft.vars ?? {}), [c.name]: e.target.value };
                    })
                  }
                />
                <label htmlFor={`c-${c.name}`}>{c.label}</label>
                <span className="adm-color-row__hex">
                  {((config.vars && config.vars[c.name]) || c.default).toLowerCase()}
                </span>
              </div>
            ))}
          </AdminSection>

          <AdminSection
            id="welcome"
            title="Welcome & nav copy"
            sub="Hero copy, nav badge, and meta line."
            onEnterView={setActiveSection}
          >
            <AdminField label="Nav badge">
              <input
                className="adm-input"
                type="text"
                value={config.content.navTag}
                onChange={(e) =>
                  update((draft) => {
                    draft.content.navTag = e.target.value;
                  })
                }
              />
            </AdminField>
            <AdminField label="Hero badge">
              <input
                className="adm-input"
                type="text"
                value={config.content.heroBadge}
                onChange={(e) =>
                  update((draft) => {
                    draft.content.heroBadge = e.target.value;
                  })
                }
              />
            </AdminField>
            <AdminField
              label="Hero headline (HTML allowed; wrap accent words in <em>)"
            >
              <textarea
                className="adm-textarea"
                rows={2}
                value={config.content.heroTitle}
                onChange={(e) =>
                  update((draft) => {
                    draft.content.heroTitle = e.target.value;
                  })
                }
              />
            </AdminField>
            <AdminField label="Hero sub">
              <textarea
                className="adm-textarea"
                rows={3}
                value={config.content.heroSub}
                onChange={(e) =>
                  update((draft) => {
                    draft.content.heroSub = e.target.value;
                  })
                }
              />
            </AdminField>
            <AdminField label="Hero meta line">
              <input
                className="adm-input"
                type="text"
                value={config.content.heroMeta}
                onChange={(e) =>
                  update((draft) => {
                    draft.content.heroMeta = e.target.value;
                  })
                }
              />
            </AdminField>
          </AdminSection>

          <AdminSection
            id="stats"
            title="Stats bar"
            sub="The four headline numbers above the questions. Number + label per stat."
            onEnterView={setActiveSection}
          >
            {config.stats.map((s, i) => (
              <div
                key={i}
                className="adm-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <input
                  className="adm-input"
                  type="text"
                  value={s.num}
                  placeholder="Number"
                  onChange={(e) =>
                    update((draft) => {
                      draft.stats[i] = { ...draft.stats[i], num: e.target.value };
                    })
                  }
                />
                <input
                  className="adm-input"
                  type="text"
                  value={s.label}
                  placeholder="Label"
                  onChange={(e) =>
                    update((draft) => {
                      draft.stats[i] = { ...draft.stats[i], label: e.target.value };
                    })
                  }
                />
              </div>
            ))}
            <StatsButtons
              stats={config.stats}
              onAdd={() =>
                update((draft) => {
                  draft.stats = [...draft.stats, { num: '', label: '' }];
                })
              }
              onRemove={() =>
                update((draft) => {
                  draft.stats = draft.stats.slice(0, -1);
                })
              }
            />
          </AdminSection>

          <AdminSection
            id="questions"
            title="Questions"
            sub={
              <>
                The 8 yes/no/unsure questions. Edit text, embedded stat, and weight values per
                answer. Negative weights subtract from Stay Score; positive weights add. <code>tag</code>{' '}
                is a stable identifier — edit only if you know what you're doing (it's referenced in
                mirror-copy).
              </>
            }
            onEnterView={setActiveSection}
          >
            {config.questions.map((q, i) => (
              <QuestionEditor
                key={q.id}
                index={i}
                question={q}
                onChange={(patch) =>
                  update((draft) => {
                    draft.questions[i] = { ...draft.questions[i], ...patch };
                  })
                }
                onWeightChange={(key, val) =>
                  update((draft) => {
                    draft.questions[i] = {
                      ...draft.questions[i],
                      weight: { ...draft.questions[i].weight, [key]: val },
                    };
                  })
                }
              />
            ))}
          </AdminSection>

          <AdminSection
            id="verdicts"
            title="Verdicts & thresholds"
            sub={
              <>
                Stay Score is 0–10. Anything ≤ <em>leave</em> threshold gets the leave verdict;
                anything ≤ <em>mid</em> threshold gets the mid verdict; everything else gets the
                stay verdict.
              </>
            }
            onEnterView={setActiveSection}
          >
            <div className="adm-grid-2">
              <AdminField label="Leave threshold (≤)">
                <input
                  className="adm-input"
                  type="number"
                  min={0}
                  max={10}
                  step={0.5}
                  value={config.thresholds.leave}
                  onChange={(e) =>
                    update((draft) => {
                      draft.thresholds.leave = parseFloat(e.target.value) || 0;
                    })
                  }
                />
              </AdminField>
              <AdminField label="Mid threshold (≤)">
                <input
                  className="adm-input"
                  type="number"
                  min={0}
                  max={10}
                  step={0.5}
                  value={config.thresholds.mid}
                  onChange={(e) =>
                    update((draft) => {
                      draft.thresholds.mid = parseFloat(e.target.value) || 0;
                    })
                  }
                />
              </AdminField>
            </div>
            {(['leave', 'mid', 'stay'] as const).map((band) => (
              <div key={band}>
                <AdminField label={`${cap(band)} verdict — label`}>
                  <input
                    className="adm-input"
                    type="text"
                    value={config.verdicts[band].label}
                    onChange={(e) =>
                      update((draft) => {
                        draft.verdicts[band].label = e.target.value;
                      })
                    }
                  />
                </AdminField>
                <AdminField label={`${cap(band)} verdict — body`}>
                  <textarea
                    className="adm-textarea"
                    rows={3}
                    value={config.verdicts[band].body}
                    onChange={(e) =>
                      update((draft) => {
                        draft.verdicts[band].body = e.target.value;
                      })
                    }
                  />
                </AdminField>
              </div>
            ))}
          </AdminSection>

          <AdminSection
            id="calc"
            title="Cost-of-staying calculation"
            sub="Multipliers and constants behind the cost projection."
            onEnterView={setActiveSection}
          >
            <h3 className="adm-subhead">Price escalation factors (over 24 months)</h3>
            <div className="adm-grid-3">
              <AdminNumberField
                label="If priceClimb = yes (PE-owned signal)"
                value={config.calc.priceEscalation.peOwned}
                step={0.05}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.priceEscalation.peOwned = v;
                  })
                }
              />
              <AdminNumberField
                label="If ownershipChange = yes"
                value={config.calc.priceEscalation.ownershipChange}
                step={0.05}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.priceEscalation.ownershipChange = v;
                  })
                }
              />
              <AdminNumberField
                label="Baseline (otherwise)"
                value={config.calc.priceEscalation.baseline}
                step={0.05}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.priceEscalation.baseline = v;
                  })
                }
              />
            </div>

            <h3 className="adm-subhead">Adaptation tax (per FTE per year)</h3>
            <div className="adm-grid-3">
              <AdminNumberField
                label="Per-FTE annual base ($)"
                value={config.calc.perFteAnnualBase}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.perFteAnnualBase = v;
                  })
                }
              />
              <AdminNumberField
                label="Boost if 5+ workarounds ($)"
                value={config.calc.perFteWorkaroundBoost}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.perFteWorkaroundBoost = v;
                  })
                }
              />
              <AdminNumberField
                label="Boost if rigid (no on rigidity Q) ($)"
                value={config.calc.perFteRigidityBoost}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.perFteRigidityBoost = v;
                  })
                }
              />
            </div>

            <h3 className="adm-subhead">Adoption decay productivity drag</h3>
            <div className="adm-grid-3">
              <AdminNumberField
                label="Drag % (decimal — 0.15 = 15%)"
                value={config.calc.productivityDragPct}
                step={0.05}
                max={1}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.productivityDragPct = v;
                  })
                }
              />
              <AdminNumberField
                label="Hourly cost ($)"
                value={config.calc.hourlyCost}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.hourlyCost = v;
                  })
                }
              />
              <AdminNumberField
                label="Hours per month"
                value={config.calc.hoursPerMonth}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.hoursPerMonth = v;
                  })
                }
              />
            </div>

            <h3 className="adm-subhead">Cost of switching — four components</h3>
            <div className="adm-grid-2">
              <AdminNumberField
                label="New vendor implementation (decimal — 0.50 = 50% of new annual subscription)"
                hint="Legal tech typical: 30–80%. Covers data migration, custom integration, training."
                value={config.calc.switchImplPctOfAnnual}
                step={0.05}
                max={2}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.switchImplPctOfAnnual = v;
                  })
                }
              />
              <AdminNumberField
                label="Parallel-run months (paying both vendors during transition)"
                value={config.calc.switchParallelMonths}
                step={0.5}
                max={12}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.switchParallelMonths = v;
                  })
                }
              />
              <AdminNumberField
                label="Internal time per FTE ($)"
                hint="Project mgmt, training, data migration, change management."
                value={config.calc.switchInternalPerFte}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.switchInternalPerFte = v;
                  })
                }
              />
              <AdminNumberField
                label="Productivity dip during transition (decimal — 0.20 = 20%)"
                value={config.calc.switchProdDipPct}
                step={0.05}
                max={1}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.switchProdDipPct = v;
                  })
                }
              />
              <AdminNumberField
                label="Productivity dip duration (months)"
                value={config.calc.switchProdDipMonths}
                step={0.5}
                max={12}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.switchProdDipMonths = v;
                  })
                }
              />
              <AdminNumberField
                label="New vendor efficiency (decimal — 0.40 = 40% lower TCO with replacement)"
                hint="Drives payback period and 3-year net value."
                value={config.calc.newVendorEfficiency}
                step={0.05}
                max={1}
                onChange={(v) =>
                  update((draft) => {
                    draft.calc.newVendorEfficiency = v;
                  })
                }
              />
            </div>
          </AdminSection>

          <AdminSection
            id="tiers"
            title="Annual subscription tiers"
            sub="Buyer picks one as context. Annual amount drives the subscription run-rate."
            onEnterView={setActiveSection}
          >
            <TiersTable
              rows={config.spendTiers}
              onChange={(i, patch) =>
                update((draft) => {
                  draft.spendTiers[i] = { ...draft.spendTiers[i], ...patch } as SpendTier;
                })
              }
            />
          </AdminSection>

          <AdminSection
            id="teams"
            title="Team size buckets"
            sub="Buyer picks one as context. Median FTE drives adaptation-tax and productivity-drag math."
            onEnterView={setActiveSection}
          >
            <TeamsTable
              rows={config.teamSizes}
              onChange={(i, patch) =>
                update((draft) => {
                  draft.teamSizes[i] = { ...draft.teamSizes[i], ...patch } as TeamSize;
                })
              }
            />
          </AdminSection>

          <AdminSection
            id="data"
            title="Data, history & reset"
            sub="Manage admin config. Reset only restores defaults in this draft — buyer-side state is in a different store and isn't touched."
            onEnterView={setActiveSection}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="adm-btn adm-btn--ghost" type="button" onClick={onExport}>
                Export JSON
              </button>
              <label className="adm-btn adm-btn--ghost" style={{ cursor: 'pointer' }}>
                Import JSON
                <input
                  type="file"
                  accept=".json,application/json"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onImport(f);
                    e.currentTarget.value = '';
                  }}
                />
              </label>
              <button
                className="adm-btn adm-btn--ghost"
                type="button"
                onClick={() => setShowResetModal(true)}
              >
                Reset draft to defaults
              </button>
            </div>
            <div className="adm-note">
              <strong>Endpoints:</strong>{' '}
              <code>GET/PUT /admin/configs/stay_or_go/draft</code>,{' '}
              <code>POST /admin/configs/stay_or_go/publish</code>,{' '}
              <code>GET /admin/configs/stay_or_go/history</code>.
            </div>

            <AdminHistoryList history={history} onRollback={onRollback} />
          </AdminSection>
        </main>
      </AdminShell>

      {showResetModal && (
        <AdminModal
          title="Reset draft to defaults?"
          body="This restores every setting on this page to the public tool's bundled defaults — in your draft only. Publish to make it live. Export first if you want a backup of the current draft."
          confirmLabel="Reset"
          confirmVariant="danger"
          onCancel={() => setShowResetModal(false)}
          onConfirm={onResetToDefaults}
        />
      )}

      {showPublishModal && (
        <AdminPublishModal
          note={publishNote}
          onChangeNote={setPublishNote}
          onCancel={() => setShowPublishModal(false)}
          onConfirm={onPublish}
        />
      )}
    </>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function StatsButtons({
  stats,
  onAdd,
  onRemove,
}: {
  stats: StatItem[];
  onAdd(): void;
  onRemove(): void;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
      <button className="adm-btn adm-btn--ghost" type="button" onClick={onAdd}>
        Add stat
      </button>
      <button
        className="adm-btn adm-btn--ghost"
        type="button"
        onClick={onRemove}
        disabled={stats.length <= 1}
      >
        Remove last
      </button>
    </div>
  );
}

function QuestionEditor({
  index,
  question,
  onChange,
  onWeightChange,
}: {
  index: number;
  question: Question;
  onChange(patch: Partial<Question>): void;
  onWeightChange(key: string, val: number): void;
}) {
  const weightKeys = Object.keys(question.weight);
  return (
    <div className="sga-qcard">
      <div className="sga-qcard__head">
        <span className="sga-qcard__num">Question {index + 1}</span>
        <span className="sga-qcard__id">
          id: {question.id} · tag: {question.tag}
        </span>
      </div>
      <label htmlFor={`q-${question.id}-q`}>Question text</label>
      <input
        className="adm-input"
        id={`q-${question.id}-q`}
        type="text"
        value={question.q}
        onChange={(e) => onChange({ q: e.target.value })}
        style={{ marginBottom: 8 }}
      />
      <label htmlFor={`q-${question.id}-stat`}>Embedded stat (HTML allowed)</label>
      <textarea
        className="adm-textarea"
        id={`q-${question.id}-stat`}
        value={question.stat}
        onChange={(e) => onChange({ stat: e.target.value })}
        style={{ marginBottom: 8 }}
      />
      <div className="sga-qcard__weights">
        {weightKeys.map((k) => (
          <div key={k}>
            <label htmlFor={`q-${question.id}-w-${k}`}>
              Weight if {k.toUpperCase()}
            </label>
            <input
              className="adm-input"
              id={`q-${question.id}-w-${k}`}
              type="number"
              value={question.weight[k]}
              onChange={(e) => onWeightChange(k, parseFloat(e.target.value) || 0)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function TiersTable({
  rows,
  onChange,
}: {
  rows: SpendTier[];
  onChange(i: number, patch: Partial<SpendTier>): void;
}) {
  const cols = '120px 1fr 1fr 140px';
  return (
    <div>
      <div
        className="sga-tiers-table__head"
        style={{ gridTemplateColumns: cols }}
      >
        <span>Label</span>
        <span>Sub-text</span>
        <span>Key (read-only)</span>
        <span>Annual ($)</span>
      </div>
      {rows.map((t, i) => (
        <div
          key={t.key + i}
          className="sga-tiers-table__row"
          style={{ gridTemplateColumns: cols }}
        >
          <input
            className="adm-input"
            type="text"
            value={t.label}
            onChange={(e) => onChange(i, { label: e.target.value })}
          />
          <input
            className="adm-input"
            type="text"
            value={t.sub}
            onChange={(e) => onChange(i, { sub: e.target.value })}
          />
          <input className="adm-input" type="text" value={t.key} disabled />
          <input
            className="adm-input"
            type="number"
            value={t.annual}
            onChange={(e) =>
              onChange(i, { annual: parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
      ))}
    </div>
  );
}

function TeamsTable({
  rows,
  onChange,
}: {
  rows: TeamSize[];
  onChange(i: number, patch: Partial<TeamSize>): void;
}) {
  const cols = '100px 1fr 1fr 140px';
  return (
    <div>
      <div
        className="sga-tiers-table__head"
        style={{ gridTemplateColumns: cols }}
      >
        <span>Label</span>
        <span>Sub-text</span>
        <span>Key (read-only)</span>
        <span>Median FTE</span>
      </div>
      {rows.map((t, i) => (
        <div
          key={t.key + i}
          className="sga-tiers-table__row"
          style={{ gridTemplateColumns: cols }}
        >
          <input
            className="adm-input"
            type="text"
            value={t.label}
            onChange={(e) => onChange(i, { label: e.target.value })}
          />
          <input
            className="adm-input"
            type="text"
            value={t.sub}
            onChange={(e) => onChange(i, { sub: e.target.value })}
          />
          <input className="adm-input" type="text" value={t.key} disabled />
          <input
            className="adm-input"
            type="number"
            value={t.median}
            onChange={(e) =>
              onChange(i, { median: parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
